import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getMyQuotation, listPaymentTerms, updateQuotationDetails } from '../api/vendorApi';
import { VpSpinner, VpStatusBadge, money, fmtDate } from '../components/VpUI';
import VpIcon from '../components/VpIcon';

const VendorQuotationDetail = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [q, setQ] = useState<any>(null);
	const [error, setError] = useState('');

	// Editable details (payment terms + delivery), allowed while still pending.
	const [paymentTerms, setPaymentTerms] = useState<any[]>([]);
	const [paymentTermId, setPaymentTermId] = useState('');
	const [expectedDelivery, setExpectedDelivery] = useState('');
	const [deliveryTerms, setDeliveryTerms] = useState('');
	const [note, setNote] = useState('');
	const [saving, setSaving] = useState(false);

	const hydrate = (data: any) => {
		setQ(data);
		setPaymentTermId(data?.paymentTermId || data?.paymentTermInfo?.paymentTermId || '');
		setExpectedDelivery(
			data?.expected_delivery ? String(data.expected_delivery).slice(0, 10) : '',
		);
		setDeliveryTerms(data?.delivery_terms || '');
		setNote(data?.note || '');
	};

	useEffect(() => {
		(async () => {
			try {
				const data = await getMyQuotation(id as string);
				hydrate(data);
			} catch (err: any) {
				setError(err?.apiMessage || 'Could not load the quotation.');
			} finally {
				setLoading(false);
			}
		})();
	}, [id]);

	useEffect(() => {
		listPaymentTerms()
			.then((terms) => setPaymentTerms(Array.isArray(terms) ? terms : []))
			.catch(() => setPaymentTerms([]));
	}, []);

	const saveDetails = async () => {
		setSaving(true);
		try {
			const updated = await updateQuotationDetails({
				_id: id,
				paymentTermId: paymentTermId || undefined,
				expected_delivery: expectedDelivery || undefined,
				delivery_terms: deliveryTerms || undefined,
				note: note || undefined,
			});
			hydrate(updated);
			toast.success('Details updated successfully!');
		} catch (err: any) {
			toast.error(err?.apiMessage || 'Could not update details.');
		} finally {
			setSaving(false);
		}
	};

	if (loading) return <VpSpinner />;
	if (error) return <div className='vp-alert vp-alert-error'>{error}</div>;

	const accepted = q?.quotation_status === 'confirmed';
	const editable = q?.quotation_status === 'pending';
	const products = q?.products || [];

	return (
		<div>
			{accepted && (
				<div className='vp-congrats'>
					<div className='vp-congrats-emoji'>
					<VpIcon name='celebrate' size={40} />
				</div>
					<div>
						<h3>Congratulations! Your quotation was accepted.</h3>
						<p>
							You can now upload your challan(s) and invoice for this order from the
							Challans &amp; Invoices section.
						</p>
					</div>
				</div>
			)}

			<div className='vp-card vp-card-pad' style={{ marginBottom: 18 }}>
				<div className='vp-row-between'>
					<div>
						<div className='vp-muted' style={{ fontSize: 13 }}>
							Quotation
						</div>
						<div style={{ fontSize: 20, fontWeight: 700 }}>{q?.quotation_no}</div>
						<div className='vp-muted' style={{ fontSize: 13, marginTop: 4 }}>
							Requisition {q?.requisition?.requisition_no || '—'} ·{' '}
							{fmtDate(q?.quotation_date || q?.createdAt)}
						</div>
					</div>
					<div style={{ textAlign: 'right' }}>
						<VpStatusBadge status={q?.quotation_status} />
						{accepted && (
							<div style={{ marginTop: 10 }}>
								<button
									type='button'
									className='vp-btn vp-btn-primary vp-input-sm'
									onClick={() => navigate('/vendor-portal/challans')}>
									Upload Challan / Invoice
								</button>
							</div>
						)}
					</div>
				</div>
			</div>

			<div className='vp-card'>
				<div className='vp-card-head'>
					<h3>Quoted Items</h3>
					<span className='vp-badge vp-badge-neutral'>
						<VpIcon name='lock' size={13} /> Locked after submit
					</span>
				</div>
				<div className='vp-table-wrap'>
					<table className='vp-table'>
						<thead>
							<tr>
								<th>Product</th>
								<th>Usage / Used For</th>
								<th>Qty</th>
								<th>Rate</th>
								<th>Discount</th>
								<th>Tax %</th>
								<th style={{ textAlign: 'right' }}>Amount</th>
							</tr>
						</thead>
						<tbody>
							{products.map((p: any) => (
								<tr key={p._id}>
									<td style={{ fontWeight: 600 }}>
										{p.productInfo?.name || p.product_description || 'Item'}
									</td>
									<td>{p?.usage_note || '—'}</td>
									<td>{p.quantity}</td>
									<td>{money(p.rate)}</td>
									<td>
										{p.discount || 0}
										{p.discountType === 'fixed' ? ' ₹' : ' %'}
									</td>
									<td>{p.tax || 0}%</td>
									<td style={{ textAlign: 'right', fontWeight: 600 }}>
										{money(p.amount_with_tax)}
									</td>
								</tr>
							))}
						</tbody>
						<tfoot>
							<tr>
								<td colSpan={6} style={{ textAlign: 'right', fontWeight: 700 }}>
									Total
								</td>
								<td style={{ textAlign: 'right', fontWeight: 800, fontSize: 16 }}>
									{money(q?.amount_total)}
								</td>
							</tr>
						</tfoot>
					</table>
				</div>
			</div>

			{/* Delivery & payment details — editable while the quote is still pending. */}
			<div className='vp-card vp-card-pad vp-mt-16'>
				<div className='vp-row-between' style={{ marginBottom: 12 }}>
					<h3 style={{ margin: 0, fontSize: 16 }}>Delivery &amp; Payment Details</h3>
					{editable ? (
						<span className='vp-badge vp-badge-pending'>Editable</span>
					) : (
						<span className='vp-badge vp-badge-neutral'>
							<VpIcon name='lock' size={13} /> Read-only
						</span>
					)}
				</div>

				{editable ? (
					<>
						<div className='vp-grid-2'>
							<div>
								<div className='vp-field'>
									<label className='vp-label'>Payment Terms</label>
									<select
										className='vp-input'
										value={paymentTermId}
										onChange={(e) => setPaymentTermId(e.target.value)}>
										<option value=''>— Select payment terms —</option>
										{paymentTerms.map((t) => (
											<option key={t._id} value={t._id}>
												{t.name}
											</option>
										))}
									</select>
								</div>
								<div className='vp-field vp-mb-0'>
									<label className='vp-label'>Expected Delivery Date</label>
									<input
										className='vp-input'
										type='date'
										value={expectedDelivery}
										onChange={(e) => setExpectedDelivery(e.target.value)}
									/>
								</div>
							</div>
							<div>
								<div className='vp-field'>
									<label className='vp-label'>Delivery Terms</label>
									<input
										className='vp-input'
										value={deliveryTerms}
										onChange={(e) => setDeliveryTerms(e.target.value)}
										placeholder='e.g. FOB, ex-works'
									/>
								</div>
								<div className='vp-field vp-mb-0'>
									<label className='vp-label'>Note</label>
									<input
										className='vp-input'
										value={note}
										onChange={(e) => setNote(e.target.value)}
										placeholder='Any note for the buyer'
									/>
								</div>
							</div>
						</div>
						<div className='vp-row-between vp-mt-16'>
							<div className='vp-muted' style={{ fontSize: 13 }}>
								You can update these details until the buyer processes your
								quotation.
							</div>
							<button
								type='button'
								className='vp-btn vp-btn-primary'
								onClick={saveDetails}
								disabled={saving}>
								{saving ? 'Saving…' : 'Save Details'}
							</button>
						</div>
					</>
				) : (
					<div className='vp-grid-2'>
						<div>
							<div className='vp-field'>
								<label className='vp-label'>Payment Terms</label>
								<div>{q?.paymentTermInfo?.name || q?.payment_terms || '—'}</div>
							</div>
							<div className='vp-field vp-mb-0'>
								<label className='vp-label'>Expected Delivery Date</label>
								<div>
									{q?.expected_delivery ? fmtDate(q.expected_delivery) : '—'}
								</div>
							</div>
						</div>
						<div>
							<div className='vp-field'>
								<label className='vp-label'>Delivery Terms</label>
								<div>{q?.delivery_terms || '—'}</div>
							</div>
							<div className='vp-field vp-mb-0'>
								<label className='vp-label'>Note</label>
								<div>{q?.note || '—'}</div>
							</div>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default VendorQuotationDetail;
